import React, { useCallback } from 'react';
import { SportsFavoritesMenuManifestazioneDto, SportsSottoVoceMenu, SportsVoceMenu } from 'types/swagger';
import { StaticMenu, seoItemHierarchy } from 'components/staticMap';
import { isMatch, isTruthy } from 'utility/functions';
import { matchesOrario, matchesQuote } from './utils';

import { DatoCmsSportNav } from 'lib/datoCms/types';
import { ElementToFilter } from '../sportNav/types';
import { SportNavAccordionList } from './sportNavAccordionList';
import { SportNavFiltersTrigger } from './sportNavFiltersTrigger';
import { SportNavHighlightItem } from './sportNavHighlightItem';
import { SportNavTotocalcio } from './sportNavTotocalcio';
import classNames from 'classnames';
import { convertInThemeLogoType } from 'lib/datoCms/utils';
import { selectMenuManifestazioni } from 'features/sport/selectors';
import styles from './SportNav.module.scss';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useTypedSelector } from 'lib/centralStore';

export type SportNavProps = {
  gruppi: Array<SportsVoceMenu>;
  className?: string;
  disciplina?: string;
  staticMenu?: seoItemHierarchy;
  totocalcio?: DatoCmsSportNav['totocalcio'] | undefined;
  manifestazioniPreferiti: Array<SportsFavoritesMenuManifestazioneDto>;
  manifestazioniInEvidenza?: DatoCmsSportNav['manifestazioniInEvidenza'];
};

export const SportNav = ({
  gruppi,
  className,
  totocalcio,
  disciplina,
  staticMenu,
  manifestazioniPreferiti,
  manifestazioniInEvidenza,
}: SportNavProps) => {
  const [labelInEvidenza, labelGiocaTotocalcio, labelAllManifestazioni] = useGetLabelsByKeys([
    'in-evidenza',
    'gioca-totocalcio',
    'label-all-manifestazioni',
  ])!;

  const { slug, isLoading } = useTypedSelector(selectMenuManifestazioni);
  const selectedFilters = useTypedSelector(({ sport }) => sport.selectedFilters);
  const [selectedQuotaMin, selectedQuotaMax] = selectedFilters.quota;
  const selectedOrario = selectedFilters.orario;

  const matchesScommesseFilters = useCallback(
    (manifestazione: ElementToFilter) => {
      if (matchesOrario(manifestazione, selectedOrario)) {
        return matchesQuote(manifestazione, selectedQuotaMin, selectedQuotaMax);
      }
      return false;
    },
    [selectedOrario, selectedQuotaMax, selectedQuotaMin]
  );

  const filteredGruppi: Array<SportsVoceMenu> = (gruppi ?? []).map((gruppo) => {
    const manifestazioni: Array<SportsSottoVoceMenu> = (gruppo?.manifestazioni ?? []).filter(
      matchesScommesseFilters
    ) as Array<SportsSottoVoceMenu>;
    return {
      ...gruppo,
      manifestazioni,
    };
  });

  const filteredManifestazioniPreferiti: Array<SportsFavoritesMenuManifestazioneDto> = (
    manifestazioniPreferiti ?? []
  )?.filter(matchesScommesseFilters);

  const renderMenu = (): JSX.Element => {
    if (disciplina) {
      return (
        <React.Fragment key={`${slug}`}>
          <SportNavFiltersTrigger>{labelAllManifestazioni}</SportNavFiltersTrigger>
          <SportNavAccordionList
            key={`Menu-${filteredGruppi.map((x) => x.id).join('-')}-${isLoading ? 'l' : 'r'}`}
            gruppi={filteredGruppi}
            isLoading={isLoading}
            manifestazioniPreferitiList={filteredManifestazioniPreferiti}
          />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={`${slug}`}>
        <SportNavFiltersTrigger disabled={true}>{labelAllManifestazioni}</SportNavFiltersTrigger>
        <StaticMenu key={`Menu-${isLoading ? 'l' : 'r'}`} item={staticMenu} onMountedDestroy={false} />
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <aside className={classNames(styles.sidebar, className ?? '')}>
        {isTruthy(manifestazioniInEvidenza?.length) && (
          <React.Fragment>
            <span className={styles.highlightedTitle}>{labelInEvidenza}</span>
            {manifestazioniInEvidenza?.map((manifestazioneInEvidenza) => {
              const { id, descrizione, link, gruppoId } = manifestazioneInEvidenza;
              const { href } = link[0];
              return <SportNavHighlightItem key={id} descrizione={descrizione} gruppoId={gruppoId} href={href} />;
            })}
          </React.Fragment>
        )}
        <div
          className={classNames(styles.wrapper, {
            [styles.withHighlighted]: isTruthy(manifestazioniInEvidenza?.length),
          })}
        >
          {renderMenu()}
        </div>
      </aside>

      {totocalcio && isMatch(disciplina, '^CALCIO$') && (
        <SportNavTotocalcio
          className={className}
          linkLabel={labelGiocaTotocalcio}
          image={convertInThemeLogoType(totocalcio?.logoTotocalcio[0]!)}
        />
      )}
    </React.Fragment>
  );
};
